import type { Clock } from "@beacon/clock";
import { BeaconClient, InMemoryKeyRegistry, type IdentityBundle, type KeyRegistry } from "@beacon/client-core";
import type { FirehoseTransport } from "@beacon/transport";
import { detectLocale, loadCatalog, type Catalog, type Locale } from "../i18n/catalog.js";
import { DEFAULT_TONE, applyToneOverlay, loadToneOverlay, TONAL_KEYS, type ToneId } from "../i18n/tone/index.js";
import { assembleDataExport, parseImportedBundle, type DataExport } from "../screens/you/identity-backup.js";
import type { HomePlace, LocalStore, PersistedState, SavedPlace, Settings } from "./store.js";

/** A transport that may expose the relay /info simulation flag (RelayTransport does). */
type InfoTransport = FirehoseTransport & { info?(): Promise<{ simulation: boolean }> };

export interface SessionDeps {
  store: LocalStore;
  transport: InfoTransport;
  registry?: KeyRegistry;
  clock: Clock;
  accepted: string[];
}

export class Session {
  #announced: boolean;
  #placeSeq = 0;
  private constructor(
    private deps: SessionDeps,
    readonly client: BeaconClient,
    public locale: Locale,
    public tone: ToneId,
    public catalog: Catalog,
    public simulation: boolean,
    public home: HomePlace | undefined,
    public browseOnly: boolean,
    public settings: Settings,
    public places: SavedPlace[],
    public consents: string[],
    private bundle: IdentityBundle | undefined,
    announced: boolean,
  ) {
    this.#announced = announced;
  }

  static async start(deps: SessionDeps): Promise<Session> {
    const persisted: PersistedState = await deps.store.load();
    const registry = deps.registry ?? new InMemoryKeyRegistry();
    let announced = false;
    let client: BeaconClient;
    let bundle: IdentityBundle | undefined = persisted.identityBundle;
    let createdAt: number = persisted.createdAt ?? 0;
    if (persisted.identityBundle) {
      ({ client } = await BeaconClient.create({
        transport: deps.transport,
        clock: deps.clock,
        registry,
        identity: persisted.identityBundle,
        // Restore path is announced=true and never calls announce(); give the registry
        // self-heal a cell to re-declare genesis at if the relay was wiped under us.
        homeCell: persisted.home?.cell,
      }));
      announced = true; // a persisted bundle means genesis is already on the log
    } else {
      const created = await BeaconClient.create({ transport: deps.transport, clock: deps.clock, registry, homeCell: persisted.home?.cell });
      client = created.client;
      bundle = created.bundle;
      createdAt = deps.clock.now(); // clock-plane join instant (ADR-13); scopes the personal commons mirror
      await deps.store.save({ identityBundle: created.bundle, createdAt });
    }
    let simulation = false;
    try {
      if (deps.transport.info) simulation = (await deps.transport.info()).simulation;
    } catch {
      /* offline → assume production (no SIMULATION banner) */
    }
    const locale = detectLocale(deps.accepted, persisted.locale);
    const tone = persisted.settings?.tone ?? DEFAULT_TONE;
    // wallclock-allow: apps/webapp excluded from gate-30 — tonal variants rotate on real time.
    const catalog = applyToneOverlay(await loadCatalog(locale), await loadToneOverlay(tone, locale), Date.now());
    const session = new Session(
      deps,
      client,
      locale,
      tone,
      catalog,
      simulation,
      persisted.home,
      !!persisted.browseOnly,
      persisted.settings ?? {},
      persisted.places ?? [],
      persisted.consents ?? [],
      bundle,
      announced,
    );
    session.#createdAtMicros = createdAt;
    return session;
  }

  /** Local account creation instant (micros), 0 when unknown (legacy identities
   *  created before this field existed). */
  #createdAtMicros = 0;
  get createdAtMicros(): number {
    return this.#createdAtMicros;
  }

  get did(): string {
    return this.client.did;
  }
  get isOnboarded(): boolean {
    return !!this.home || this.browseOnly;
  }

  /** First call announces genesis with the home cell, then persists. */
  async setHome(home: HomePlace): Promise<void> {
    if (!this.#announced) {
      await this.client.announce(home.cell);
      this.#announced = true;
    }
    this.home = home;
    await this.deps.store.save({ home });
  }

  /** Browse-only path (graceful denial): announce against a chosen place cell. */
  async completeBrowse(cell: string): Promise<void> {
    if (!this.#announced) {
      await this.client.announce(cell);
      this.#announced = true;
    }
    this.browseOnly = true;
    await this.deps.store.save({ browseOnly: true });
  }

  async setLocale(locale: Locale): Promise<void> {
    this.locale = locale;
    // wallclock-allow: apps/webapp excluded from gate-30.
    this.catalog = applyToneOverlay(await loadCatalog(locale), await loadToneOverlay(this.tone, locale), Date.now());
    await this.deps.store.save({ locale });
  }

  // --- You tab (C6) --------------------------------------------------------

  /** Merge-persist local settings (radius/quiet-hours/min-severity/notifications). */
  async saveSettings(patch: Settings): Promise<void> {
    this.settings = { ...this.settings, ...patch };
    await this.deps.store.save({ settings: this.settings });
  }

  /** Advance the unread cursor for a help request and persist (merge into settings). */
  async markHelpRead(requestId: string, seq: number): Promise<void> {
    const prev = this.settings.helpReadCursors ?? {};
    if ((prev[requestId] ?? -1) >= seq) return; // never move the cursor backwards
    await this.saveSettings({ helpReadCursors: { ...prev, [requestId]: seq } });
  }

  /** Set the Voice overlay: persist into settings + recompute the catalog.
   *  A re-render then applies the new voice everywhere, since components read `catalog`. */
  async setTone(tone: ToneId): Promise<void> {
    this.tone = tone;
    this.settings = { ...this.settings, tone };
    // wallclock-allow: apps/webapp excluded from gate-30.
    this.catalog = applyToneOverlay(await loadCatalog(this.locale), await loadToneOverlay(tone, this.locale), Date.now());
    await this.deps.store.save({ settings: this.settings });
  }

  /** Recompute the catalog at `nowMs` so multi-variant tonal lines (e.g. the dark voice)
   *  rotate while the app stays open. Returns true if any tonal string changed. */
  async refreshTone(nowMs: number): Promise<boolean> {
    const next = applyToneOverlay(await loadCatalog(this.locale), await loadToneOverlay(this.tone, this.locale), nowMs);
    const changed = TONAL_KEYS.some((k) => next[k] !== this.catalog[k]);
    this.catalog = next;
    return changed;
  }

  /** Add a saved place; generates a monotonic id (cell + session sequence). */
  async addPlace(place: { cell: string; radiusKm: number; placeName?: string }): Promise<SavedPlace> {
    const id = `${place.cell}:${this.#placeSeq++}`;
    const saved: SavedPlace = { id, ...place };
    this.places = [...this.places, saved];
    await this.deps.store.save({ places: this.places });
    return saved;
  }

  /** Remove a saved place by id. */
  async removePlace(id: string): Promise<void> {
    this.places = this.places.filter((p) => p.id !== id);
    await this.deps.store.save({ places: this.places });
  }

  /** Edit a saved place in place (by id), preserving its array position. */
  async updatePlace(id: string, patch: { cell?: string; radiusKm?: number; placeName?: string }): Promise<void> {
    this.places = this.places.map((p) => (p.id === id ? { ...p, ...patch } : p));
    await this.deps.store.save({ places: this.places });
  }

  /** Reorder a saved place by swapping it with its neighbour. Array order is the
   *  canonical zone order (drives the Now "Other watched places" list). */
  async reorderPlace(id: string, dir: "up" | "down"): Promise<void> {
    const arr = [...this.places];
    const i = arr.findIndex((p) => p.id === id);
    if (i < 0) return;
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    this.places = arr;
    await this.deps.store.save({ places: this.places });
  }

  /** Persist the live-follow opt-in + travel radius. */
  async setLiveFollow(liveFollow: { enabled: boolean; radiusKm: number }): Promise<void> {
    await this.saveSettings({ liveFollow });
  }

  /** Mirror an on-wire consent grant/withdraw into the local granted-purpose set. */
  async recordConsent(purpose: string, action: "grant" | "withdraw"): Promise<void> {
    const set = new Set(this.consents);
    if (action === "grant") set.add(purpose);
    else set.delete(purpose);
    this.consents = [...set];
    await this.deps.store.save({ consents: this.consents });
  }

  /** Portable DSAR export (privacy §6) of everything held on this device. */
  exportState(): DataExport {
    return assembleDataExport({
      did: this.did,
      bundle: this.bundle ?? ({ did: this.did } as IdentityBundle),
      home: this.home,
      locale: this.locale,
      consents: this.consents,
      settings: this.settings,
    });
  }

  /** Adopt a rotated/restored recovery bundle so the next launch restores this identity. */
  async adoptBundle(bundle: IdentityBundle): Promise<void> {
    this.bundle = bundle;
    await this.deps.store.save({ identityBundle: bundle });
  }

  /** Validate an imported recovery file, then persist it for the next launch. */
  async adoptRecovery(text: string): Promise<void> {
    const bundle = await parseImportedBundle(text);
    await this.adoptBundle(bundle);
  }

  /** Crypto-shred local wipe: erase device-held identity + settings (privacy §6 erasure). */
  async wipeLocal(): Promise<void> {
    await this.deps.store.clear();
  }
}
